import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  Skeleton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Actions, useDispatchProvider, useStateDrawerNotifications, useStateHeader } from '../utils/StateProvider';
import { usePermissions } from '../utils/AclProvider';
import { collection, getCountFromServer, getDoc, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { formatDate, getNewDateTime } from '../../utils/utils';
import { getDocs } from '@firebase/firestore';

const DrawerNotifications = () => {
  const drawerNotifications = useStateDrawerNotifications();
  const dispatch = useDispatchProvider();
  const handleDrawerClose = () => {
    dispatch && dispatch({ type: Actions.DrawerNotifications, payload: false });
  };

  return (
    <Drawer
      open={drawerNotifications}
      onClose={handleDrawerClose}
      anchor={'right'}
    >
      <Box sx={{ width: '336px' }}>
        <AppBar position="sticky">
          <Toolbar color={'none'}>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="close"
              sx={{ mr: 2, ml: -2.5 }}
              onClick={handleDrawerClose}
            >
              <Close />
            </IconButton>
            <Typography
              variant="h6"
              component="div"
              sx={{ flexGrow: 1 }}
            >
              Notifications
            </Typography>
          </Toolbar>
        </AppBar>
        <List>
          <ListProductsBackorder />
          <Divider />
          <ListOrdersShipping />
        </List>
      </Box>
    </Drawer>
  );
};

const ListProductsBackorder = () => {
  const stateHeader = useStateHeader();
  const [stateProducts, setStateProducts] = useState([]);
  const [stateLoading, setStateLoading] = useState(true);
  const drawerNotifications = useStateDrawerNotifications();
  const dispatch = useDispatchProvider();

  const currentDate = getNewDateTime();

  const collectionRef = query(
    collection(db, 'products'),
    where('productStatus', '==', 'backorder'),
    where('dateBackorder', '<=', currentDate),
    orderBy('dateUpdated', 'desc'),
  );
  // const collectionRef = collection(db, 'orders');

  // const [productsSnapshot, loading, error] = useCollection(collectionRef, {
  //   snapshotListenOptions: { includeMetadataChanges: false },
  // });

  const handleViewOrder = (product) => async () => {
    dispatch && dispatch({ type: Actions.FormOrder, payload: product.orderRef.id });
    dispatch && dispatch({ type: Actions.TabsOrder, payload: 'products' });
    dispatch && dispatch({ type: Actions.DrawerOrder, payload: true });
  };

  useEffect(() => {
    const countProducts = async () => {
      const productsSnapshot = await getCountFromServer(collectionRef);
      // setStateCount();
      dispatch &&
        dispatch({
          type: Actions.HeaderNotifications,
          payload: {
            backorders: productsSnapshot?.data().count,
          },
        });
    };
    countProducts().then();
  }, []);

  useEffect(() => {
    const getProducts = async () => {
      const productsSnapshot = await getDocs(collectionRef);
      const productsList = productsSnapshot?.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
      setStateProducts(productsList);
    };
    if (drawerNotifications) {
      setStateLoading(true);
      getProducts().then(() => {
        setStateLoading(false);
      });
    }
  }, [drawerNotifications]);

  return (
    <>
      <ListItem>
        <ListItemText
          primary={'Backorder products.'}
          slotProps={{ primary: { variant: 'subtitle1' } }}
        />
      </ListItem>
      {stateLoading && (
        <ListItem>
          <ListItemText
            primary={
              <Skeleton
                variant="text"
                sx={{ fontSize: '1rem' }}
              />
            }
            secondary={
              <Skeleton
                variant="text"
                sx={{ fontSize: '1rem' }}
              />
            }
          />
        </ListItem>
      )}
      {!stateLoading && stateProducts.length === 0 && (
        <ListItem>
          <ListItemText
            primary={'No results found.'}
            slotProps={{ primary: { variant: 'body1' } }}
          />
        </ListItem>
      )}
      {!stateLoading &&
        stateProducts.length > 0 &&
        stateProducts.map((product) => (
          <ListItem
            key={product.id}
            data-id={product.id}
            disablePadding
          >
            <ListItemButton onClick={handleViewOrder(product)}>
              <ListItemText
                primary={product.description}
                secondary={formatDate(product.dateBackorder)}
                slotProps={{ primary: { variant: 'body1' }, secondary: { color: 'primary', variant: 'subtitle2' } }}
              />
            </ListItemButton>
          </ListItem>
        ))}
    </>
  );
};

const ListOrdersShipping = () => {
  const stateHeader = useStateHeader();
  const [stateProducts, setStateProducts] = useState([]);
  const [stateLoading, setStateLoading] = useState(true);
  const drawerNotifications = useStateDrawerNotifications();
  const dispatch = useDispatchProvider();

  // const currentDate = getNewDateTime();

  const collectionRef = query(
    collection(db, 'orders'),
    where('orderStatus', '==', 'shipping'),
    orderBy('dateUpdated', 'desc'),
  );
  // const collectionRef = collection(db, 'orders');

  // const [productsSnapshot, loading, error] = useCollection(collectionRef, {
  //   snapshotListenOptions: { includeMetadataChanges: false },
  // });

  const handleViewOrder = (product) => async () => {
    dispatch && dispatch({ type: Actions.FormOrder, payload: product.id });
    dispatch && dispatch({ type: Actions.TabsOrder, payload: 'form' });
    dispatch && dispatch({ type: Actions.DrawerOrder, payload: true });
  };

  useEffect(() => {
    const countProducts = async () => {
      const productsSnapshot = await getCountFromServer(collectionRef);
      // setStateCount();
      dispatch &&
        dispatch({
          type: Actions.HeaderNotifications,
          payload: {
            shipping: productsSnapshot?.data().count,
          },
        });
    };
    countProducts().then();
  }, []);

  useEffect(() => {
    const getProducts = async () => {
      const productsSnapshot = await getDocs(collectionRef);
      const productsList = await Promise.all(
        productsSnapshot?.docs.map(async (doc) => {
          const orderData = doc.data();
          let customer = null;
          try {
            if (orderData?.customerRef) {
              const customerSnap = await getDoc(orderData.customerRef);
              if (customerSnap.exists()) {
                customer = { id: customerSnap.id, ...customerSnap.data() };
              }
            }
          } catch (e) {
            console.error('Failed to fetch customer for order', doc.id, e);
          }
          return { id: doc.id, ...orderData, customer };
        }),
      );
      setStateProducts(productsList);
    };
    if (drawerNotifications) {
      setStateLoading(true);
      getProducts().then(() => {
        setStateLoading(false);
      });
    }
  }, [drawerNotifications]);

  return (
    <>
      <ListItem>
        <ListItemText
          primary={'Shipping orders'}
          slotProps={{ primary: { variant: 'subtitle1' } }}
        />
      </ListItem>
      {stateLoading && (
        <ListItem>
          <ListItemText
            primary={
              <Skeleton
                variant="text"
                sx={{ fontSize: '1rem' }}
              />
            }
            secondary={
              <Skeleton
                variant="text"
                sx={{ fontSize: '1rem' }}
              />
            }
          />
        </ListItem>
      )}
      {!stateLoading && stateProducts.length === 0 && (
        <ListItem>
          <ListItemText
            primary={'No results found.'}
            slotProps={{ primary: { variant: 'body1' } }}
          />
        </ListItem>
      )}
      {!stateLoading &&
        stateProducts.length > 0 &&
        stateProducts.map((product) => (
          <ListItem
            key={product.id}
            data-id={product.id}
            disablePadding
          >
            <ListItemButton onClick={handleViewOrder(product)}>
              <ListItemText
                primary={`${product.customer?.name} - ${product.customer?.phone}`}
                secondary={formatDate(product.dateShipping)}
                slotProps={{ primary: { variant: 'body1' }, secondary: { color: 'primary', variant: 'subtitle2' } }}
              />
            </ListItemButton>
          </ListItem>
        ))}
    </>
  );
};

export default DrawerNotifications;
